/**
 * Medication Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Pill, Save, Plus, Trash2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

export function MedicationOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    action: "administer",
    medications: [{ 
      name: "", 
      dose: "", 
      route: "po", 
      time_given: "", 
      effectiveness: "",
      notes: "" 
    }],
    prn_reason: "",
    pain_before: "",
    pain_after: "",
    patient_response: "",
    side_effects: [],
    md_notification_needed: false,
    md_notification_reason: "",
  });

  const routes = [
    { value: "po", label: "PO (Oral)" },
    { value: "sl", label: "SL (Sublingual)" },
    { value: "pr", label: "PR (Rectal)" },
    { value: "iv", label: "IV (Intravenous)" },
    { value: "sq", label: "SQ (Subcutaneous)" },
    { value: "im", label: "IM (Intramuscular)" },
    { value: "topical", label: "Topical" },
    { value: "patch", label: "Transdermal Patch" },
    { value: "inhaled", label: "Inhaled" },
    { value: "nasal", label: "Nasal" },
  ];

  const sideEffects = [
    "Drowsiness", "Nausea", "Constipation", "Confusion", "Respiratory depression",
    "Itching", "Dry mouth", "Dizziness", "Hallucinations", "None observed"
  ];

  const addMedication = () => {
    setFormData({
      ...formData,
      medications: [...formData.medications, { name: "", dose: "", route: "po", time_given: "", effectiveness: "", notes: "" }]
    });
  };

  const removeMedication = (index) => {
    setFormData({
      ...formData,
      medications: formData.medications.filter((_, i) => i !== index)
    });
  };

  const updateMedication = (index, field, value) => {
    const newMeds = [...formData.medications];
    newMeds[index][field] = value;
    setFormData({ ...formData, medications: newMeds });
  };

  const toggleSideEffect = (effect) => {
    if (formData.side_effects.includes(effect)) {
      setFormData({
        ...formData,
        side_effects: formData.side_effects.filter(e => e !== effect)
      });
    } else {
      setFormData({
        ...formData,
        side_effects: [...formData.side_effects, effect]
      });
    }
  };

  const handleSave = () => {
    if (formData.medications.some(m => !m.name || !m.dose)) {
      toast.error("Please fill in medication name and dose");
      return;
    }
    toast.success("Medication administration recorded");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="medication-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Pill className="w-5 h-5 text-violet-400" />
        <h3 className="font-semibold text-white">Medication Administration</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Action Type</Label>
        <Select value={formData.action} onValueChange={(v) => setFormData({...formData, action: v})}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="administer">Administer Medication</SelectItem>
            <SelectItem value="prn">PRN Medication Given</SelectItem>
            <SelectItem value="hold">Medication Held</SelectItem>
            <SelectItem value="refuse">Patient Refused</SelectItem>
            <SelectItem value="reconcile">Medication Reconciliation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Medications</Label>
          <Button variant="ghost" size="sm" onClick={addMedication} className="text-teal-400 hover:text-teal-300">
            <Plus className="w-4 h-4 mr-1" />Add Medication
          </Button>
        </div>

        {formData.medications.map((med, index) => (
          <div key={index} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-300">Medication {index + 1}</p>
              {formData.medications.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeMedication(index)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Input 
                  value={med.name} 
                  onChange={(e) => updateMedication(index, "name", e.target.value)} 
                  placeholder="Medication name" 
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>
              <Input 
                value={med.dose} 
                onChange={(e) => updateMedication(index, "dose", e.target.value)} 
                placeholder="Dose" 
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select value={med.route} onValueChange={(v) => updateMedication(index, "route", v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {routes.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-slate-500" />
                <Input 
                  type="time" 
                  value={med.time_given} 
                  onChange={(e) => updateMedication(index, "time_given", e.target.value)} 
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Select value={med.effectiveness} onValueChange={(v) => updateMedication(index, "effectiveness", v)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Effect..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="effective">Effective</SelectItem>
                  <SelectItem value="partial">Partially Effective</SelectItem>
                  <SelectItem value="ineffective">Ineffective</SelectItem>
                  <SelectItem value="pending">Pending Assessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {formData.action === "prn" && (
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-200">PRN Reason</Label>
            <Input 
              value={formData.prn_reason} 
              onChange={(e) => setFormData({...formData, prn_reason: e.target.value})} 
              placeholder="Reason given" 
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200">Pain Before (0-10)</Label>
            <Input 
              type="number" 
              min="0" 
              max="10" 
              value={formData.pain_before} 
              onChange={(e) => setFormData({...formData, pain_before: e.target.value})} 
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-200">Pain After (0-10)</Label>
            <Input 
              type="number" 
              min="0" 
              max="10" 
              value={formData.pain_after} 
              onChange={(e) => setFormData({...formData, pain_after: e.target.value})} 
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-slate-200">Side Effects Observed</Label>
        <div className="flex flex-wrap gap-1.5">
          {sideEffects.map((effect) => (
            <Badge 
              key={effect}
              variant={formData.side_effects.includes(effect) ? "default" : "outline"}
              className={`cursor-pointer transition-colors ${
                formData.side_effects.includes(effect) 
                  ? effect === "None observed" ? "bg-green-600" : "bg-amber-600" 
                  : "border-slate-600 text-slate-300 hover:bg-slate-700"
              }`}
              onClick={() => toggleSideEffect(effect)}
            >
              {effect}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Patient Response</Label>
        <Textarea 
          value={formData.patient_response} 
          onChange={(e) => setFormData({...formData, patient_response: e.target.value})} 
          placeholder="Document patient's response to medication..." 
          rows={2} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <div>
            <Label className="text-slate-200">MD Notification Needed?</Label>
            <p className="text-xs text-slate-400">For adverse reactions or concerns</p>
          </div>
        </div>
        <Switch checked={formData.md_notification_needed} onCheckedChange={(c) => setFormData({...formData, md_notification_needed: c})} />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Save Record</Button>
      </div>
    </div>
  );
}

export default MedicationOverlay;
